import React, { useContext } from "react";

import { UserContext } from "../../contexts";
import { ownsListing } from "../../views/Listing/InternalDetails.jsx";
import {
  resolveOriginalMoney,
  useFormatMoneyWithBase,
} from "../../core/currency";

const EMPTY = "—";

/**
 * Back face of the listing card — "how the watch was bought".
 *
 * Every field here is already on the card's `listing` prop: POST
 * /api/listings/search selects `listings.*`, so buying price/currency, serial
 * number, supplier, the purchase + payment dates, all four cost buckets and the
 * private note arrive with the grid. No per-card fetch. (The three things that
 * would need GET /api/listings/{id} — the Xero PO file, uploaded invoices and
 * the sell-request backlink — are deliberately NOT shown here.)
 *
 * Money is rendered through resolveOriginalMoney + useFormatMoneyWithBase, not
 * the gbp_* pivot: the pivot round-trips a hand-typed figure through two
 * conversions (1000 USD → gbp 747 → 993 USD). Costs are summed as one bucket
 * because that is how they are entered and reconciled.
 */
export const hasInternalFace = (user, listing, activeStoreId) =>
  ownsListing(user, listing, activeStoreId);

const Fact = ({ label, value, mono }) => (
  <div className="rc-lcard-adapter__int-fact">
    <span className="rc-lcard-adapter__int-label">{label}</span>
    <span className={`rc-lcard-adapter__int-value${mono ? " rc-lcard-adapter__int-value--mono" : ""}`}>
      {value || EMPTY}
    </span>
  </div>
);

// No close control of its own: the whole back face is the flip-back target (see
// RCListingCard), so a button here would just be a second way to do the same
// thing — and one the rep has to aim at.
const ListingInternalFace = ({ listing }) => {
  const { user, activeStoreId } = useContext(UserContext);
  const fm = useFormatMoneyWithBase();

  // Mirrors the API's own $ownsListing gate. A foreign-store admin gets no
  // face at all rather than a column of "—" (see ownsListing's docblock).
  if (!hasInternalFace(user, listing, activeStoreId)) return null;

  const bought = resolveOriginalMoney(listing, "buying_price", "buying_currency");
  const costs = resolveOriginalMoney(
    listing,
    ["polishing", "accessories", "shipping", "travel"],
    "costs_currency"
  );

  const money = (m) => (m && m.amount ? fm(m.amount, m.currency, { decimals: 0 }) : null);

  // Dates are plain date strings/timestamps; render them in the app's usual
  // short form rather than raw ISO.
  const date = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const costLines = [
    ["Polishing", listing.polishing],
    ["Accessories", listing.accessories],
    ["Shipping", listing.shipping],
    ["Travel", listing.travel],
  ].filter(([, v]) => Number(v) > 0);

  const note = (listing.private_note || "").trim();

  return (
    <div className="rc-lcard-adapter__internal">
      <div className="rc-lcard-adapter__int-head">
        <span className="rc-lcard-adapter__int-eyebrow">Internal details</span>
        <span className="rc-lcard-adapter__int-back-hint">Click to go back</span>
      </div>

      <div className="rc-lcard-adapter__int-keys">
        <Fact label="Bought for" value={money(bought)} />
        <Fact label="Serial" value={listing.serial_number} mono />
      </div>

      <div className="rc-lcard-adapter__int-keys">
        <Fact label="Supplier" value={listing.supplier} />
        <Fact
          label="Payment date"
          value={date(listing.supplier_payment_date) || date(listing.date_purchase)}
        />
      </div>

      <div className="rc-lcard-adapter__int-costs">
        <div className="rc-lcard-adapter__int-costs-head">
          <span className="rc-lcard-adapter__int-label">Costs</span>
          <span className="rc-lcard-adapter__int-value">{money(costs) || EMPTY}</span>
        </div>
        {costLines.length > 0 && (
          <ul className="rc-lcard-adapter__int-costlist">
            {costLines.map(([label, value]) => (
              <li key={label}>
                <span>{label}</span>
                <span>{fm(value, costs?.currency, { decimals: 0 })}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {note && (
        <div className="rc-lcard-adapter__int-note" title={note}>
          <span className="rc-lcard-adapter__int-label">Note</span>
          <span className="rc-lcard-adapter__int-notetext">{note}</span>
        </div>
      )}
    </div>
  );
};

export default ListingInternalFace;
